import { ALLOCATION, BUCKETING_NEW_ALLOCATION, BUCKETING_VARIATION_CACHE, GET_THIRD_PARTY_SEGMENT, THIRD_PARTY_SEGMENT_URL } from '../enum/FlagshipConstant';
import { LogLevel } from '../enum/index';
import { TargetingOperator, TroubleshootingLabel } from '../types';
import { errorFormat, logDebugSprintf, logError, sprintf } from '../utils/utils';
import { DecisionManager } from './DecisionManager';
import { Segment } from '../hit/Segment.ts';
import { Troubleshooting } from '../hit/Troubleshooting.ts';
export class BucketingManager extends DecisionManager {
    _murmurHash;
    _sdkManager;
    get _bucketingContent() {
        return this._sdkManager.getBucketingContent();
    }
    constructor({ httpClient, config, murmurHash, sdkManager, trackingManager, flagshipInstanceId }) {
        super({
            httpClient,
            config,
            trackingManager,
            flagshipInstanceId
        });
        this._murmurHash = murmurHash;
        this._sdkManager = sdkManager;
    }
    async sendContext(visitor) {
        try {
            if (Object.keys(visitor.context).length <= 3 || !visitor.hasConsented || !visitor.hasContextBeenUpdated) {
                return;
            }
            visitor.hasContextBeenUpdated = false;
            const SegmentHit = new Segment({
                context: visitor.context,
                visitorId: visitor.visitorId,
                anonymousId: visitor.anonymousId
            });
            await visitor.sendHit(SegmentHit);
            const hitTroubleshooting = new Troubleshooting({
                label: TroubleshootingLabel.VISITOR_SEND_HIT,
                logLevel: LogLevel.INFO,
                traffic: visitor.traffic || 0,
                visitorId: visitor.visitorId,
                visitorSessionId: visitor.instanceId,
                flagshipInstanceId: visitor.sdkInitialData?.instanceId,
                anonymousId: visitor.anonymousId,
                config: this.config,
                hitContent: SegmentHit.toApiKeys()
            });
            visitor.segmentHitTroubleshooting = hitTroubleshooting;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            logError(this.config, error.message || error, 'sendContext');
        }
    }
    async getThirdPartySegment(visitorId) {
        const url = sprintf(THIRD_PARTY_SEGMENT_URL, this.config.envId, visitorId);
        const now = Date.now();
        const contexts = {};
        try {
            const response = await this._httpClient.getAsync(url, { nextFetchConfig: this.config.nextFetchConfig });
            const content = response.body;
            if (Array.isArray(content)) {
                for (const item of content) {
                    contexts[`${item.partner}::${item.segment}`] = item.value;
                }
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            logError(this.config, errorFormat(error.message || error, {
                url,
                nextFetchConfig: this.config.nextFetchConfig,
                duration: Date.now() - now
            }), GET_THIRD_PARTY_SEGMENT);
        }
        return contexts;
    }
    async getCampaignsAsync(visitor) {
        if (!this._bucketingContent) {
            return null;
        }
        const troubleshooting = this._bucketingContent?.accountSettings?.troubleshooting;
        this.troubleshooting = undefined;
        if (troubleshooting) {
            this.troubleshooting = {
                startDate: new Date(troubleshooting.startDate),
                endDate: new Date(troubleshooting.endDate),
                timezone: troubleshooting.timezone,
                traffic: troubleshooting.traffic
            };
        }
        if (this._bucketingContent.panic) {
            this.panic = true;
            return [];
        }
        this.panic = false;
        if (!this._bucketingContent.campaigns) {
            return null;
        }
        if (this.config.fetchThirdPartyData) {
            const thirdPartySegments = await this.getThirdPartySegment(visitor.visitorId);
            visitor.updateContext(thirdPartySegments);
        }
        await this.sendContext(visitor);
        let visitorCampaigns = [];
        this._bucketingContent.campaigns.forEach(campaign => {
            const currentCampaigns = this.getVisitorCampaigns(campaign.variationGroups, campaign.id, campaign.type, visitor);
            if (currentCampaigns) {
                currentCampaigns.slug = campaign.slug ?? null;
                currentCampaigns.name = campaign.name;
                visitorCampaigns?.push(currentCampaigns);
            }
        });
        visitorCampaigns = this.applyCampaignsForcedAllocation(visitor, visitorCampaigns);
        visitorCampaigns = this.applyCampaignsForcedUnallocation(visitor, visitorCampaigns);
        return visitorCampaigns;
    }
    getVisitorCampaigns(variationGroups, campaignId, campaignType, visitor) {
        for (const variationGroup of variationGroups) {
            const check = this.checkVisitorMatchesTargeting(variationGroup, visitor);
            if (check) {
                const variation = this.getVariation(variationGroup, visitor);
                if (!variation) {
                    return null;
                }
                return {
                    id: campaignId,
                    variation,
                    variationGroupId: variationGroup.id,
                    variationGroupName: variationGroup.name,
                    type: campaignType
                };
            }
        }
        return null;
    }
    getVariation(variationGroup, visitor) {
        const hash = this._murmurHash.murmurHash3Int32(variationGroup.id + visitor.visitorId);
        const hashAllocation = hash % 100;
        let totalAllocation = 0;
        for (const variation of variationGroup.variations) {
            const assignmentsHistory = visitor.visitorCache?.data?.assignmentsHistory;
            const cacheVariationId = assignmentsHistory ? assignmentsHistory[variationGroup.id] : null;
            if (cacheVariationId) {
                const newVariation = variationGroup.variations.find(x => x.id === cacheVariationId);
                if (!newVariation) {
                    continue;
                }
                logDebugSprintf(this.config, ALLOCATION, BUCKETING_VARIATION_CACHE, visitor.visitorId, newVariation.id);
                return {
                    id: newVariation.id,
                    name: newVariation.name,
                    modifications: newVariation.modifications,
                    reference: newVariation.reference
                };
            }
            if (variation.allocation === undefined || variation.allocation === 0) {
                continue;
            }
            totalAllocation += variation.allocation;
            if (hashAllocation < totalAllocation) {
                logDebugSprintf(this.config, ALLOCATION, BUCKETING_NEW_ALLOCATION, visitor.visitorId, variation.id, totalAllocation);
                return {
                    id: variation.id,
                    modifications: variation.modifications,
                    reference: variation.reference,
                    name: variation.name
                };
            }
        }
        return null;
    }
    checkVisitorMatchesTargeting(variationGroup, visitor) {
        if (!variationGroup || !variationGroup.targeting || !variationGroup.targeting.targetingGroups) {
            return false;
        }
        // OR logic: visitor matches if ANY targeting group matches
        return variationGroup.targeting.targetingGroups.some(targetingGroup => this.checkAllTargetingRulesMatch(targetingGroup.targetings, visitor));
    }
    checkAllTargetingRulesMatch(targetings, visitor) {
        if (!targetings || targetings.length === 0) {
            return false;
        }
        // AND logic: ALL targeting rules must match
        return targetings.every(targeting => this.matchesTargetingCriteria(targeting, visitor));
    }
    matchesArrayTargeting(targeting, visitorData) {
        if (!Array.isArray(targeting.value)) {
            return false;
        }
        const notOperator = [TargetingOperator.NOT_EQUALS, TargetingOperator.NOT_CONTAINS].includes(targeting.operator);
        return notOperator
            ? targeting.value.every((val) => this.matchesTargetingCriteria({
                ...targeting,
                value: val
            }, visitorData))
            : targeting.value.some((val) => this.matchesTargetingCriteria({
                ...targeting,
                value: val
            }, visitorData));
    }
    matchesTargetingCriteria(targeting, visitor) {
        if (targeting.key === 'fs_all_users') {
            return true;
        }
        if (Array.isArray(targeting.value)) {
            return this.matchesArrayTargeting(targeting, visitor);
        }
        const visitorValue = targeting.key === 'fs_users'
            ? visitor.visitorId
            : visitor.context[targeting.key];
        return this.evaluateOperator(targeting.operator, visitorValue, targeting.value);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    evaluateEqualityOperator(operator, visitorValue, targetValue) {
        if (operator === TargetingOperator.EQUALS) {
            return visitorValue === targetValue;
        }
        return visitorValue !== targetValue;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    evaluateStringOperator(operator, visitorValue, targetValue) {
        const visitorStr = visitorValue.toString();
        const targetStr = targetValue.toString();
        switch (operator) {
            case TargetingOperator.CONTAINS:
                return visitorStr.includes(targetStr);
            case TargetingOperator.NOT_CONTAINS:
                return !visitorStr.includes(targetStr);
            case TargetingOperator.STARTS_WITH:
                return visitorStr.startsWith(targetStr);
            case TargetingOperator.ENDS_WITH:
                return visitorStr.endsWith(targetStr);
            default:
                return false;
        }
    }
    evaluateExistenceOperator(operator, visitorValue) {
        if (operator === TargetingOperator.EXISTS) {
            return visitorValue !== undefined && visitorValue !== null;
        }
        return visitorValue === undefined || visitorValue === null;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    evaluateComparisonOperator(operator, visitorValue, targetValue) {
        switch (operator) {
            case TargetingOperator.GREATER_THAN:
                return visitorValue > targetValue;
            case TargetingOperator.LOWER_THAN:
                return visitorValue < targetValue;
            case TargetingOperator.GREATER_THAN_OR_EQUALS:
                return visitorValue >= targetValue;
            case TargetingOperator.LOWER_THAN_OR_EQUALS:
                return visitorValue <= targetValue;
            default:
                return false;
        }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    evaluateOperator(operator, visitorValue, targetValue) {
        if (operator === TargetingOperator.EQUALS || operator === TargetingOperator.NOT_EQUALS) {
            return this.evaluateEqualityOperator(operator, visitorValue, targetValue);
        }
        if (operator === TargetingOperator.EXISTS || operator === TargetingOperator.NOT_EXISTS) {
            return this.evaluateExistenceOperator(operator, visitorValue);
        }
        if (operator === TargetingOperator.CONTAINS || operator === TargetingOperator.NOT_CONTAINS ||
            operator === TargetingOperator.STARTS_WITH || operator === TargetingOperator.ENDS_WITH) {
            return this.evaluateStringOperator(operator, visitorValue, targetValue);
        }
        if (operator === TargetingOperator.GREATER_THAN || operator === TargetingOperator.LOWER_THAN ||
            operator === TargetingOperator.GREATER_THAN_OR_EQUALS || operator === TargetingOperator.LOWER_THAN_OR_EQUALS) {
            return this.evaluateComparisonOperator(operator, visitorValue, targetValue);
        }
        return false;
    }
}
